/// <amd-module name="Artifacts/Package/components/package-details-common/PackageProvenance" />
import { PackageAttribute } from "Artifacts/Package/components/package-details-common/PackageAttribute";
import { CiConstants, ProvenanceSource } from "Artifacts/Package/Constants";
import * as PackageRes from "Artifacts/Package/Resources";
import { IFeedProvenanceDataService } from "Artifacts/Package/services/ProvenanceDataService";
import {
  IFeedProvenanceLinkService,
  IProvenanceLinkData,
  IProvenanceLinks,
} from "Artifacts/Package/services/ProvenanceLinkService";
import { ExternalLink } from "Artifacts/Root/Components/ExternalLink";
import { InternalLink } from "Artifacts/Root/Components/InternalLink";
import { CustomerIntelligenceHelper } from "Artifacts/Root/helpers/CustomerIntelligenceHelper";
import { getFeedNameWithNewView } from "Artifacts/Root/Helpers/FeedHelpers";
import * as Utils_Html from "Artifacts/Root/helpers/HtmlHelper";
import * as RootRes from "Artifacts/Root/Resources";
import { IFeed } from "Artifacts/Root/Types/IFeed";
import { PackageVersionProvenance, Provenance } from "Artifacts/Root/web-apis/contracts/Feed";
import { Build } from "Build/Client/Contracts/Build";
import { RepositoryTypes } from "Build/SourceProviders/Common";
import * as React from "react";
import * as StringUtils from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Icon } from "VSSUI/Icon";
import { ago } from "VSSUI/Utilities/Date";

export interface IProvenanceProps {
  pageContext: IVssPageContext;
  feed: IFeed;
  packageId: string;
  versionId: string;
  publishDate: Date;
  isProvenanceEnabled: boolean;
  isProvenanceSupported: boolean;
}

export interface IProvenanceState {
  provenance: Provenance;
  build: Build;
  links: IProvenanceLinks;
  repoIcon: string;
}

export class PackageProvenance extends React.Component<IProvenanceProps, IProvenanceState> {
  private _mounted: boolean;

  constructor(props: IProvenanceProps) {
    super(props);
    this.state = ({ provenance: null } as unknown) as IProvenanceState;
    this._mounted = false;
  }

  public componentDidMount(): void {
    this._mounted = true;
    this.initialize();
  }

  public componentWillUnmount(): void {
    this._mounted = false;
  }

  public render(): JSX.Element {
    return (
      <div className="package-provenance">
        <div className="package-group-spacer" />
        <PackageAttribute title={PackageRes.PackageAttributeTitle_Provenance}>
          {!this.showBuild() &&
            !this.showRelease() &&
            !this.showExternal() &&
            this.renderGenericPublisherSection()}
          {this.showBuild() && this.renderBuildSection()}
          {this.showRelease() && this.renderReleaseSection()}
          {this.showExternal() && this.renderExternalSection()}
          {this.showCode() && (
            <div>
              <div className="package-group-spacer" />
              {this.renderCodeSection()}
            </div>
          )}
        </PackageAttribute>
      </div>
    );
  }

  private async initialize(): Promise<void> {
    // we should consider putting provenance in the package version rather than recalculating it every time.
    if (this.props.isProvenanceEnabled && this.props.isProvenanceSupported) {
      const provenanceDataService = this.props.pageContext.getService<IFeedProvenanceDataService>(
        "IFeedProvenanceDataService",
      );
      const provenanceLinkService = this.props.pageContext.getService<IFeedProvenanceLinkService>(
        "IFeedProvenanceLinkService",
      );
      const feedId = getFeedNameWithNewView(this.props.feed.name, this.props.feed.selectedViewId);
      let provenance: Provenance = {} as Provenance;
      let links: IProvenanceLinks | null = null;
      let repoIcon = "";

      try {
        const projectId = this.props.feed.project ? this.props.feed.project.id : undefined;
        const pvp: PackageVersionProvenance = await provenanceDataService.getPackageVersionProvenance(
          projectId,
          feedId,
          this.props.packageId,
          this.props.versionId,
        );
        provenance = pvp.provenance;
        links = await provenanceLinkService.getLinksAsync(
          provenance,
          this.props.feed.accountMetadata.publicAccessMapping,
        );
        repoIcon = this.getRepoIcon(links.repositoryType);
      } catch (err) {
        if (err != null && err.status === 404) {
          // 404s fetching provenance or links are expected
        } else {
          throw err;
        }
      } finally {
        if (this._mounted === true && links) {
          this.setState({ provenance, links, repoIcon });

          if (this.showBuild()) {
            CustomerIntelligenceHelper.publishEvent(
              this.props.pageContext,
              CiConstants.ProvenanceShowBuild,
              {
                feedId: this.props.feed.id,
              },
            );
          } else if (this.showRelease()) {
            CustomerIntelligenceHelper.publishEvent(
              this.props.pageContext,
              CiConstants.ProvenanceShowRelease,
              {
                feedId: this.props.feed.id,
              },
            );
          } else if (this.showExternal()) {
            CustomerIntelligenceHelper.publishEvent(
              this.props.pageContext,
              CiConstants.ProvenanceShowExternal,
              {
                feedId: this.props.feed.id,
              },
            );
          }
        }
      }
    }
  }

  private renderGenericPublisherSection(): JSX.Element {
    return (
      <div>
        <div className="package-group-header" role="heading" aria-level={3}>
          {RootRes.Publisher}
        </div>
        {this.canShowProvenance() && (
          <div className="package-attribute-item">
            <Icon className="package-attribute-item-icon" iconName="Contact" />
            {this.state.provenance.data && (
              <span
                className="user-name"
                dangerouslySetInnerHTML={this.getProvenanceUserDisplayName()}
              />
            )}
            {this.state.provenance.userAgent && (
              <span>
                {StringUtils.format(
                  PackageRes.PackageProvenance_UsingUserAgent,
                  this.state.provenance.userAgent,
                )}
              </span>
            )}
          </div>
        )}
        {this.renderPublishDate()}
        <div className="package-group-spacer" />
      </div>
    );
  }

  private getProvenanceUserDisplayName() {
    return {
      __html:
        this.state.provenance.data["Common.IdentityDisplayName"] ||
        PackageRes.PackageProvenance_UnknownUser,
    };
  }

  private renderPublishDate(): JSX.Element {
    return (
      <div className="package-attribute-item">
        <Icon className="package-attribute-item-icon" iconName="Clock" />
        <span>
          {ago(this.props.publishDate) +
            " (" +
            this.props.publishDate.toLocaleDateString() +
            " " +
            this.props.publishDate.toLocaleTimeString() +
            ")"}
        </span>
      </div>
    );
  }

  private renderBuildSection(): JSX.Element {
    return (
      <div>
        <div className="package-group-header" role="heading" aria-level={3}>
          {RootRes.Build}
        </div>
        <div className="package-attribute-item">
          {this.hasLink(this.state.links.buildDefinitionLink) && (
            <span>
              <Icon iconName="Build" className="package-attribute-item-icon" />
              {this.state.links.buildDefinitionLink &&
                this.renderLink(
                  this.state.links.buildDefinitionLink,
                  StringUtils.format(
                    PackageRes.AriaLabel_ProvenanceBuildDefinition,
                    this.state.links.buildDefinitionLink.text,
                  ),
                  CiConstants.ProvenanceBuildDefinitionLinkClicked,
                )}
            </span>
          )}
          {this.hasLink(this.state.links.buildLink) &&
            this.hasLink(this.state.links.buildDefinitionLink) && (
              <span className="separator">/</span>
            )}
          {this.state.links.buildLink &&
            this.hasLink(this.state.links.buildLink) &&
            this.renderLink(
              this.state.links.buildLink,
              StringUtils.format(
                PackageRes.AriaLabel_ProvenanceBuild,
                this.state.links.buildLink.text,
              ),
              CiConstants.ProvenanceBuildLinkClicked,
            )}
        </div>
        {this.renderPublishDate()}
      </div>
    );
  }

  private renderCodeSection(): JSX.Element {
    return (
      <div>
        <div className="package-group-header" role="heading" aria-level={3}>
          {RootRes.Code}
        </div>
        <div className="package-attribute-item">
          {this.state.links.commitLink && this.hasLink(this.state.links.commitLink) && (
            <span>
              <Icon iconName="BranchCommit" className="package-attribute-item-icon" />
              {this.renderLink(
                this.state.links.commitLink,
                StringUtils.format(
                  PackageRes.AriaLabel_ProvenanceCodeCommit,
                  this.state.links.commitLink.text,
                ),
                CiConstants.ProvenanceCommitLinkClicked,
              )}
            </span>
          )}
          {this.hasLink(this.state.links.commitLink) &&
            this.hasLink(this.state.links.branchLink) && (
              <span className="separator">{RootRes.In}</span>
            )}
          {this.state.links.branchLink && this.hasLink(this.state.links.branchLink) && (
            <span>
              <Icon iconName="BranchFork2" className="package-attribute-item-inline-icon" />
              {this.renderLink(
                this.state.links.branchLink,
                StringUtils.format(
                  PackageRes.AriaLabel_ProvenanceCodeBranch,
                  this.state.links.branchLink.text,
                ),
                CiConstants.ProvenanceBranchLinkClicked,
              )}
            </span>
          )}
        </div>
        {this.state.links.repositoryLink && this.hasLink(this.state.links.repositoryLink) && (
          <div className="package-attribute-item">
            <span>
              <Icon iconName={this.state.repoIcon} className="package-attribute-item-icon" />
              {this.renderLink(
                this.state.links.repositoryLink,
                StringUtils.format(
                  PackageRes.AriaLabel_ProvenanceCodeRepository,
                  this.state.links.repositoryLink.text,
                ),
                CiConstants.ProvenanceRepositoryLinkClicked,
              )}
            </span>
          </div>
        )}
      </div>
    );
  }

  private renderReleaseSection(): JSX.Element {
    return (
      <div>
        <div className="package-group-header" role="heading" aria-level={3}>
          {RootRes.Release}
        </div>
        <div className="package-attribute-item">
          {this.hasLink(this.state.links.releaseDefinitionLink) && (
            <span>
              <Icon iconName="Rocket" className="package-attribute-item-icon" />
              {this.state.links.releaseDefinitionLink &&
                this.renderLink(
                  this.state.links.releaseDefinitionLink,
                  StringUtils.format(
                    PackageRes.AriaLabel_ProvenanceReleaseDefinition,
                    this.state.links.releaseDefinitionLink.text,
                  ),
                  CiConstants.ProvenanceReleaseDefinitionLinkClicked,
                )}
            </span>
          )}
          {this.hasLink(this.state.links.releaseLink) &&
            this.hasLink(this.state.links.releaseDefinitionLink) && (
              <span className="separator">/</span>
            )}
          {this.hasLink(this.state.links.releaseLink) && (
            <span>
              {this.state.links.releaseLink &&
                this.renderLink(
                  this.state.links.releaseLink,
                  StringUtils.format(
                    PackageRes.AriaLabel_ProvenanceRelease,
                    this.state.links.releaseLink.text,
                  ),
                  CiConstants.ProvenanceReleaseLinkClicked,
                )}
            </span>
          )}
        </div>
        {this.renderPublishDate()}
      </div>
    );
  }

  private renderExternalSection(): JSX.Element {
    return (
      <div>
        <div className="package-group-header" role="heading" aria-level={3}>
          {RootRes.External}
        </div>
        <div className="package-attribute-item">
          {this.state.links.externalLink != null && (
            <span>
              <Icon iconName="NavigateExternalInline" className="package-attribute-item-icon" />
              {this.renderLink(
                this.state.links.externalLink,
                StringUtils.format(
                  PackageRes.AriaLabel_ProvenanceExternal,
                  this.state.links.externalLink.text,
                ),
                CiConstants.ProvenanceExternalLinkClicked,
              )}
            </span>
          )}
        </div>
        {this.renderPublishDate()}
      </div>
    );
  }

  private renderLink(
    link: IProvenanceLinkData | null,
    ariaLabel: string,
    ciFeature: string,
  ): JSX.Element | null {
    if (link == null || link.text == null) {
      return null;
    }

    const decodedLinkText = Utils_Html.htmlDecode(link.text);

    if (link.url == null) {
      return <span aria-label={ariaLabel}>{link.text}</span>;
    }

    if (link.isExternal) {
      try {
        return (
          <ExternalLink
            pageContext={this.props.pageContext}
            href={link.url}
            linkAriaLabel={ariaLabel}
            ciFeature={ciFeature}
          >
            {decodedLinkText}
          </ExternalLink>
        );
      } catch (error) {
        if (error instanceof URIError) {
          return (
            <div className="provenanceUriFailureText">
              Resources.PackageProvenance_LinkRenderingFailed
            </div>
          );
        } else {
          throw error;
        }
      }
    }

    return (
      <InternalLink
        pageContext={this.props.pageContext}
        href={link.url}
        linkAriaLabel={ariaLabel}
        ciFeature={ciFeature}
      >
        {decodedLinkText}
      </InternalLink>
    );
  }

  private hasLink(link: IProvenanceLinkData | null): boolean {
    return link != null && link.text != null;
  }

  private canShowProvenance(): boolean {
    return (
      this.props.isProvenanceEnabled &&
      this.props.isProvenanceSupported &&
      this.state.provenance != null
    );
  }

  private showBuild(): boolean {
    return (
      this.canShowProvenance() &&
      StringUtils.equals(
        this.state.provenance.provenanceSource,
        ProvenanceSource.InternalBuild,
        true,
      ) &&
      this.state.links != null &&
      (this.state.links.buildLink != null || this.state.links.buildDefinitionLink != null)
    );
  }

  private showRelease(): boolean {
    return (
      this.canShowProvenance() &&
      StringUtils.equals(
        this.state.provenance.provenanceSource,
        ProvenanceSource.InternalRelease,
        true,
      ) &&
      this.state.links != null &&
      (this.state.links.releaseLink != null || this.state.links.releaseDefinitionLink != null)
    );
  }

  private showExternal(): boolean {
    return (
      this.canShowProvenance() &&
      StringUtils.equals(this.state.provenance.provenanceSource, ProvenanceSource.External, true) &&
      this.state.links != null &&
      this.state.links.externalLink != null
    );
  }

  private showCode(): boolean {
    return (
      this.canShowProvenance() &&
      this.showBuild() &&
      this.state.links != null &&
      (this.state.links.repositoryLink != null ||
        this.state.links.commitLink != null ||
        this.state.links.branchLink != null)
    );
  }

  private getRepoIcon(repoType: string | null) {
    if (repoType) {
      if (StringUtils.equals(repoType, RepositoryTypes.TfsVersionControl, true)) {
        return "TFVCLogo";
      }
      if (StringUtils.equals(repoType, RepositoryTypes.TfsGit, true)) {
        return "GitLogo";
      }
      if (
        StringUtils.equals(repoType, RepositoryTypes.GitHub, true) ||
        StringUtils.equals(repoType, RepositoryTypes.GitHubEnterprise, true)
      ) {
        return "GitHubLogo";
      }
      if (StringUtils.equals(repoType, RepositoryTypes.ExternalGit, true)) {
        return "ExternalGit";
      }
      if (StringUtils.equals(repoType, RepositoryTypes.Svn, true)) {
        return "SVNLogo";
      }
      if (StringUtils.equals(repoType, RepositoryTypes.Bitbucket, true)) {
        return "BitbucketLogo32";
      }
    }
    // Generic
    return "Repo";
  }
}
